import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { ApplicationState } from '../../../../../store';
import { CustomHeading } from 'shared-components';
import Field from '../../../../Field/Field';
import { CustomCollapse } from '../../../../CustomCollapse/CustomCollapse';
import { PrevNextButtons } from '../../../../PrevNextButtons';

import {
  VisaOrPermitType,
  CourseCompare,
  VisaType,
  RefusedVisaType,
  FeeStatus,
  IUkStudentVisaSchema,
  IUkNonStudentVisaSchema,
} from 'types';
import { useNavigate } from 'react-router-dom';
import ApplicantHeader from '../../../../../components/molecules/applicantHeader';
import { SectionHeader, SectionTitles } from '@/components/molecules/SectionHeader';
import { getApplicationData } from '@/components/pages/applicant-dashboard/sections/helpers/getApplicationData';
import { combineUnevenArrays } from '../helpers';
import { formatYearMonthDate } from '@/utilities/dateHelpers';
import { useTaskRouteGuard } from '@/hooks/useTaskRouteGuard';
import { TaskLoadingSkeleton } from '@/components/molecules/TaskLoadingSkeleton';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';
import { useTranslation } from "react-i18next";

export default function ImmigrationHistory() {
    const { t } = useTranslation('common');
  // Route guard - redirects if task is disabled
  const { isLoading } = useTaskRouteGuard('/immigration-history');
  const navigate = useNavigate();
  const { application } = useSelector((state: ApplicationState) => state.apply);
  const { nextRoute, previousRoute } = useTaskNavigation();

  if (isLoading) {
    return <TaskLoadingSkeleton />;
  }

  const { initial, current } = getApplicationData(application);
  const {
    hasNonStudentVisaOrPermit,
    ukNonStudentVisaOrPermit,
    feeStatus,
    isVisaRequired,
    visaExemptionReason,
    hasVisaForCurrentOrPreviousUkStudy,
    hasStudentVisaForThisUkStudy,
    ukStudentVisas,
    nationalityForApplicationPurposes,
    hasPreviousVisaRefusal,
    ukVisaRefusals,
    hasPreviousNonUkVisaRefusal,
    nonUkVisaRefusals,
    hasEuEeaSwitzerlandExperience,
    hasEuFamilyMember,
  } = current;

  const {
    hasNonStudentVisaOrPermit: initialHasNonStudentVisaOrPermit,
    ukNonStudentVisaOrPermit: initialUkNonStudentVisaOrPermit,
    feeStatus: initialFeeStatus,
    isVisaRequired: initialIsVisaRequired,
    visaExemptionReason: initialVisaExemptionReason,
    hasVisaForCurrentOrPreviousUkStudy: initialHasVisaForCurrentOrPreviousUkStudy,
    hasStudentVisaForThisUkStudy: initialHasStudentVisaForThisUkStudy,
    ukStudentVisas: initialUkStudentVisas,
    nationalityForApplicationPurposes: initialNationalityForApplicationPurposes,
    hasPreviousVisaRefusal: initialHasPreviousVisaRefusal,
    ukVisaRefusals: initialUkVisaRefusals,
    hasPreviousNonUkVisaRefusal: initialHasPreviousNonUkVisaRefusal,
    nonUkVisaRefusals: initialNonUkVisaRefusals,
    hasEuEeaSwitzerlandExperience: initialHasEuEeaSwitzerlandExperience,
    hasEuFamilyMember: initialHasEuFamilyMember,
  } = initial;

  const isNotAHomeStudent = feeStatus !== FeeStatus.Home || initialFeeStatus !== FeeStatus.Home;

  const isVisaExempt = !isVisaRequired || !initialIsVisaRequired;

  const hasUkVisaForStudy = hasVisaForCurrentOrPreviousUkStudy && hasStudentVisaForThisUkStudy;
  const initialHasUkVisaForStudy = initialHasVisaForCurrentOrPreviousUkStudy && initialHasStudentVisaForThisUkStudy;

  const hasVisa = hasUkVisaForStudy || initialHasUkVisaForStudy;

  const ukStudentVisasReal = hasUkVisaForStudy ? ukStudentVisas : [];
  const initialUKStudentVisasReal = initialHasUkVisaForStudy ? initialUkStudentVisas : [];

  const combinedUkStudentVisas = combineUnevenArrays<IUkStudentVisaSchema>(
    ukStudentVisasReal,
    initialUKStudentVisasReal,
  );

  const isNotBritishOrIrish =
    !isBritishOrIrish(nationalityForApplicationPurposes) || !isBritishOrIrish(initialNationalityForApplicationPurposes);

  const showNonStudentVisaOrPermit = hasNonStudentVisaOrPermit || initialHasNonStudentVisaOrPermit;

  const ukNonStudentVisaOrPermitReal = hasNonStudentVisaOrPermit ? ukNonStudentVisaOrPermit : [];
  const initialUkNonStudentVisaOrPermitReal = initialHasNonStudentVisaOrPermit ? initialUkNonStudentVisaOrPermit : [];

  const combinedUkNonStudentVisaOrPermit = combineUnevenArrays<IUkNonStudentVisaSchema>(
    ukNonStudentVisaOrPermitReal,
    initialUkNonStudentVisaOrPermitReal,
  );

  const showPreviousVisaRefusals = hasPreviousVisaRefusal || initialHasPreviousVisaRefusal;
  const showPreviousNonUkVisaRefusals = hasPreviousNonUkVisaRefusal || initialHasPreviousNonUkVisaRefusal;

  const ukVisaRefusalsReal = hasPreviousVisaRefusal ? ukVisaRefusals : [];
  const initialUkVisaRefusalsReal = initialHasPreviousVisaRefusal ? initialUkVisaRefusals : [];

  const combinedUkVisaRefusals = combineUnevenArrays(ukVisaRefusalsReal, initialUkVisaRefusalsReal);

  const nonUkVisaRefusalsReal = hasPreviousNonUkVisaRefusal ? nonUkVisaRefusals : [];
  const initialNonUkVisaRefusalsReal = initialHasPreviousNonUkVisaRefusal ? initialNonUkVisaRefusals : [];

  const combinedNonUkVisaRefusals = combineUnevenArrays(nonUkVisaRefusalsReal, initialNonUkVisaRefusalsReal);

  return (
    <>
      <ApplicantHeader pageName="Application" />
      <Box>
        <SectionHeader title={SectionTitles.IMMIGRATION_HISTORY} />

        <CustomHeading level="h2" mb={2} mt={6} fontSize="md">
          {t('common:data')}
                          </CustomHeading>

        <Box>
          {isNotAHomeStudent && (
            <>
              {/* Fields for general information about the student that is not elaborated upon with dropdown sections */}
              <Field leftLabel="Student visa required" value={isVisaRequired} originalValue={initialIsVisaRequired} />
              {isVisaExempt && (
                <Field leftLabel="Reason" value={visaExemptionReason} originalValue={initialVisaExemptionReason} />
              )}
              <Field
                leftLabel="Previous UK study"
                value={hasVisaForCurrentOrPreviousUkStudy ?? false}
                originalValue={initialHasVisaForCurrentOrPreviousUkStudy ?? false}
              />
              <Field
                leftLabel="Lived/worked in EU/EEA/Switzerland"
                value={hasEuEeaSwitzerlandExperience ?? false}
                originalValue={initialHasEuEeaSwitzerlandExperience ?? false}
              />
              <Field
                leftLabel="Parent/guardian/partner EU/EEA/Swiss"
                value={hasEuFamilyMember ?? false}
                originalValue={initialHasEuFamilyMember ?? false}
              />

              {/* Field as heading and custom collapse for UK student visa aquired for this study,
              this layout makes the frontend less noisy when there are an excess of edited attributes */}
              <Field
                leftLabel="Has UK student visa for this study"
                value={hasUkVisaForStudy ?? false}
                originalValue={initialHasUkVisaForStudy ?? false}
              />
              {hasVisa &&
                combinedUkStudentVisas.map((visa, index) => (
                  <CustomCollapse
                    key={`${visa.current?.studentVisaType ?? visa.original?.studentVisaType}-${index}`}
                    defaultIsOpen
                  >
                    <CustomCollapse.Trigger>{`UK student visa:  ${
                      VisaType[visa.current?.studentVisaType] ?? VisaType[visa.original?.studentVisaType]
                    }`}</CustomCollapse.Trigger>

                    <CustomCollapse.Content>
                      <Field
                        leftLabel="Type of visa"
                        value={VisaType[visa.current?.studentVisaType]}
                        originalValue={VisaType[visa.original?.studentVisaType]}
                      />
                      <Field
                        leftLabel="Level of study"
                        value={visa.current?.levelOfStudy}
                        originalValue={visa.original?.levelOfStudy}
                      />
                      <Field
                        leftLabel="Year"
                        value={visa.current?.visaReceivedYear}
                        originalValue={visa.original?.visaReceivedYear}
                      />
                      <Field
                        leftLabel="Completed"
                        value={visa.current?.isCourseCompleted ?? false}
                        originalValue={visa.original?.isCourseCompleted ?? false}
                      />
                      <Field
                        leftLabel="Application course level"
                        value={CourseCompare[visa.current?.compareCourse]}
                        originalValue={CourseCompare[visa.original?.compareCourse]}
                      />
                    </CustomCollapse.Content>
                  </CustomCollapse>
                ))}
            </>
          )}

          {/* Field as heading and custom collapse for holding a UK non-student visa or permit,
              this layout makes the frontend less noisy when there are an excess of edited attributes */}
          <Field
            leftLabel="Do you currently hold a UK permit or a non-student visa?"
            value={hasNonStudentVisaOrPermit ?? false}
            originalValue={initialHasNonStudentVisaOrPermit ?? false}
          />
          {isNotBritishOrIrish &&
            showNonStudentVisaOrPermit &&
            combinedUkNonStudentVisaOrPermit.map((visaOrPermit, index) => (
              <CustomCollapse
                key={`${visaOrPermit.current?.studentVisaType ?? visaOrPermit.original?.studentVisaType}-${index}`}
                defaultIsOpen
              >
                <CustomCollapse.Trigger>{`UK permit or non-student visa:  ${
                  VisaOrPermitType[visaOrPermit.current?.studentVisaType] ??
                  VisaOrPermitType[visaOrPermit.original?.studentVisaType]
                }`}</CustomCollapse.Trigger>

                <CustomCollapse.Content>
                  <Field
                    leftLabel="Visa type"
                    value={VisaOrPermitType[visaOrPermit.current?.studentVisaType]}
                    originalValue={VisaOrPermitType[visaOrPermit.original?.studentVisaType]}
                  />
                  <Field
                    leftLabel="Issued"
                    value={formatYearMonthDate(visaOrPermit.current?.issuedDate)}
                    originalValue={formatYearMonthDate(visaOrPermit.original?.issuedDate)}
                  />
                  <Field
                    leftLabel="Valid until"
                    value={formatYearMonthDate(visaOrPermit.current?.validTill)}
                    originalValue={formatYearMonthDate(visaOrPermit.original?.validTill)}
                  />
                </CustomCollapse.Content>
              </CustomCollapse>
            ))}

          {isNotAHomeStudent && (
            <>
              {/* Field as heading and custom collapse for previous UK visa refusals,
              this layout makes the frontend less noisy when there are an excess of edited attributes */}
              <Field
                leftLabel="Has previous UK visa refusals"
                value={hasPreviousVisaRefusal ?? false}
                originalValue={initialHasPreviousVisaRefusal ?? false}
              />
              {showPreviousVisaRefusals &&
                combinedUkVisaRefusals.map((refusal, index) => (
                  <CustomCollapse
                    key={`${refusal.current?.refusedVisaType ?? refusal.original?.refusedVisaType}-${index}`}
                    defaultIsOpen
                  >
                    <CustomCollapse.Trigger>{`UK visa refusal:  ${
                      RefusedVisaType[refusal.current?.refusedVisaType] ??
                      RefusedVisaType[refusal.original?.refusedVisaType]
                    }`}</CustomCollapse.Trigger>

                    <CustomCollapse.Content>
                      <Field
                        leftLabel="Visa type"
                        value={refusal.current?.refusedVisaType}
                        originalValue={refusal.original?.refusedVisaType}
                      />
                      <Field
                        leftLabel="Year"
                        value={refusal.current?.visaRefusalYear}
                        originalValue={refusal.original?.visaRefusalYear}
                      />
                    </CustomCollapse.Content>
                  </CustomCollapse>
                ))}

              {/* Field as heading and custom collapse for non-UK student visa aquired for this study,
              this layout makes the frontend less noisy when there are an excess of edited attributes */}
              <Field
                leftLabel="Has previous non-UK visa refusals"
                value={hasPreviousNonUkVisaRefusal ?? false}
                originalValue={initialHasPreviousNonUkVisaRefusal ?? false}
              />
              {showPreviousNonUkVisaRefusals &&
                combinedNonUkVisaRefusals.map((refusal, index) => (
                  <CustomCollapse
                    key={`${refusal.current?.visaRefusalCountry ?? refusal.original?.visaRefusalCountry}-${index}`}
                    defaultIsOpen
                  >
                    <CustomCollapse.Trigger>{`Non-UK visa refusals  ${
                      refusal.current?.visaRefusalCountry ?? refusal.original?.visaRefusalCountry
                    }`}</CustomCollapse.Trigger>

                    <CustomCollapse.Content>
                      <Field
                        leftLabel="Was student visa"
                        value={refusal.current?.isStudentVisaRefused}
                        originalValue={refusal.original?.isStudentVisaRefused}
                      />
                      <Field
                        leftLabel="Year"
                        value={refusal.current?.visaRefusalYear}
                        originalValue={refusal.original?.visaRefusalYear}
                      />
                    </CustomCollapse.Content>
                  </CustomCollapse>
                ))}
            </>
          )}
        </Box>

        <Box mt={6}>
          <PrevNextButtons
            onPrevious={previousRoute ? () => navigate(previousRoute) : undefined}
            onNext={nextRoute ? () => navigate(nextRoute) : undefined}
          />
        </Box>
      </Box>
    </>
  );
}

const isBritishOrIrish = (nationalityForApplicationPurposes: string): boolean => {
  const exemptNationalities = ['British', 'Irish'].map(e => e.toLowerCase());
  const nationalityForApplication = (nationalityForApplicationPurposes ?? '')?.toLowerCase();

  return exemptNationalities.includes(nationalityForApplication);
};
